/* eslint-disable react-hooks/set-state-in-effect -- pre-existing pattern flagged by the React
   Compiler rules added in eslint-plugin-react-hooks v7; refactoring it is
   out of scope for the lint toolchain upgrade. */
import React from 'react'

import type { LinkAttachmentLayout, LinkAttachmentStatus } from '../../types'

import type { LinkAttachmentVariant } from './CardShell'
import { isPlayableAudio } from './CardThumbnail'
import { HEX_PATTERN, toCssHex } from './hex'

/** White on the dark stop, near-black on the light stop and the audio strip. */
const INK_ON_DARK = '#FFFFFF'
/** The near-black used across the Figma frames. */
const INK_ON_LIGHT = '#040403'

const INK_BY_VARIANT: Record<LinkAttachmentVariant, string> = {
  dark: INK_ON_DARK,
  light: INK_ON_LIGHT,
}

export interface ChinPaletteInput {
  variant: LinkAttachmentVariant
  accentColor?: string
  status?: LinkAttachmentStatus
  layout?: LinkAttachmentLayout
  thumbnailUrl?: string
  /**
   * Together with `sourceUrl`, identifies the playable-audio card whose hero
   * region is the native player rather than an image. See `hasHero` below.
   */
  mimeType?: string
  sourceUrl?: string
}

export interface ChinPalette {
  /**
   * Hero URL to render. Cleared once the image has errored so `CardThumbnail`
   * falls through to its type-icon placeholder instead of a broken `<img>`.
   */
  thumbnailUrl?: string
  /**
   * Raw extracted colour to publish as the `--accent` custom property on the
   * shell and the tail, or `undefined` when there is no paintable accent. The
   * card keeps its hue and swaps the lightness in CSS (`styles.css`).
   */
  accentHex?: string
  /**
   * Ink for both chin lines: white on the sender's dark stop, near-black on the
   * receiver's pale stop and on the audio strip. Fixed per surface — the stops
   * clear AA at every hue.
   */
  chinTextColor: string
  /**
   * The surface the card actually paints against, for callers whose colours must
   * match it (the CTA button). Equals `variant` except on the audio strip, whose
   * hero is the light `AUDIO_BG_CLASS` regardless of side — so a `dark` audio card
   * paints a light surface and the CTA must follow it, the way `chinTextColor`
   * already does for the chin text.
   */
  surface: LinkAttachmentVariant
  /** True while the card renders as a skeleton. */
  isLoading: boolean
  /** Hand to `CardThumbnail` so a broken hero drops the accent with it. */
  onImageError: () => void
}

/**
 * Resolves the accent a link card publishes and tracks hero-image failure.
 *
 * The accent is a claim about an image the viewer can see, so it only paints
 * when `CardThumbnail` actually mounts that image. Mirroring its branches, the
 * hero is *not* the accent's source when:
 *
 * - `layout: 'classic'` — there is no hero region at all;
 * - `status` is not `'ready'` — the hero is the pulsing skeleton, no image yet;
 * - `thumbnailUrl` is absent — the hero is the type-icon placeholder, or for a
 *   playable video a bare `bg-black` box with no poster;
 * - the attachment is playable audio — the hero collapses to the native
 *   `<audio>` player and the `<img>` never mounts even though `thumbnailUrl` is
 *   set, so `onImageError` can never rescue it and the gate has to be up front;
 * - the image 404s — an accent extracted from something the viewer cannot see.
 *   `onImageError` covers both hero shapes: the `<img>` reports directly, and a
 *   playable video's `poster` (which raises no event of its own) is verified by
 *   the hidden probe image in `CardThumbnail`.
 *
 * Every excluded case publishes no accent and keeps the variant's own fill.
 */
export function useChinPalette({
  variant,
  accentColor,
  status = 'ready',
  layout = 'featured',
  thumbnailUrl,
  mimeType,
  sourceUrl,
}: ChinPaletteInput): ChinPalette {
  const [heroFailed, setHeroFailed] = React.useState(false)

  // A new hero URL is a new chance to load — otherwise a card that recycles
  // (same element, different attachment) would stay stuck on the placeholder.
  React.useEffect(() => {
    setHeroFailed(false)
  }, [thumbnailUrl])

  const onImageError = React.useCallback(() => {
    setHeroFailed(true)
  }, [])

  // Truthiness rather than `!= null` so this tracks `CardThumbnail`'s own
  // `thumbnailUrl ?` branch, which renders the placeholder for `''` too.
  //
  // Playable audio stays excluded: its hero collapses to the native `<audio>`
  // player, so the artwork never appears and an accent taken from it would claim
  // an image the viewer cannot see. The `<img>` never mounts either, so
  // `onImageError` could not withdraw the colour if the artwork 404'd.
  const isAudio = isPlayableAudio(mimeType, sourceUrl)
  const hasHero =
    layout === 'featured' && !!thumbnailUrl && !isAudio && !heroFailed
  const canDerive = hasHero && status === 'ready'
  // Only a validated hex is published: the extracted `accent_color` is always a
  // hex, and CSS would resolve any colour name, so narrow the input here. Bare
  // hexes are accepted because the extractor omits the `#` often enough that
  // rejecting them would silently lose the colour.
  const accentHex =
    canDerive && accentColor != null && HEX_PATTERN.test(accentColor.trim())
      ? toCssHex(accentColor)
      : undefined

  // The accent surface and the sender's dark fallback both take white ink; the
  // pale receiver fallback and the imposed audio strip both take near-black — so
  // the ink is fixed per surface.
  const chinTextColor = isAudio ? INK_ON_LIGHT : INK_BY_VARIANT[variant]
  const surface: LinkAttachmentVariant = isAudio ? 'light' : variant

  return {
    thumbnailUrl: heroFailed ? undefined : thumbnailUrl,
    accentHex,
    chinTextColor,
    surface,
    isLoading: status === 'loading',
    onImageError,
  }
}
